import { revalidateGetOperationsByMe } from "@/modules/operations/(trenes)/infraestructure/getOperationByMe";
import { useQueryClient } from "@tanstack/react-query";
import { auth } from "@/config/firebaseApp";
import { operation_url } from "@/config/endpoints";
import { IOperation } from "@umahealth/entities";
import { useCallback, useEffect, useRef, useState } from "react";
import { Socket } from "socket.io-client";
import { progressStatus, finishedStatus } from "../useGetCall";
import { invalidarListadoDeOperaciones } from "src/services/react-query/invalidarListadoDeOperaciones";

export interface PendingCall {
	id: string;
	operationId: IOperation["id"];
	doctorName: string;
	inCall: boolean;
	status: "in-progress" | "finished";
	apto?: boolean;
	operator?: IOperation["user"];
	timestamp: number;
}

export function usePendingCalls(socket: Socket | null) {
	const [calls, setCalls] = useState<PendingCall[]>([]);
	const queryClient = useQueryClient();

	/**
	 * Operaciones cuya tarjeta de "revisión finalizada" el enfermero ya cerró.
	 *
	 * Hace falta desde que la relectura trae del servidor las recién cerradas:
	 * sin esto, cerrar la tarjeta la haría reaparecer en el siguiente ciclo.
	 * Va por ref y no por estado porque sólo se consulta dentro de la
	 * relectura; no tiene que provocar un render.
	 */
	const descartadas = useRef<Set<number>>(new Set());

	const dismissCall = useCallback((id: string) => {
		setCalls((prev) => {
			const cerrada = prev.find((c) => c.id === id);
			if (cerrada?.status === "finished") {
				descartadas.current.add(cerrada.operationId);
			}
			return prev.filter((c) => c.id !== id);
		});
	}, []);

	useEffect(() => {
		if (!socket) return;

		socket.emit("apt-review-notification-subscription");

		// Hydrate pending calls from active operations when socket connects
		// Lo consulta el temporizador para decidir cada cuánto releer.
		const hayEsperando = { current: false };

		const hydrate = async () => {
			try {
				// Wait for Firebase auth to be ready
				const user = await new Promise<typeof auth.currentUser>((resolve) => {
					if (auth.currentUser) return resolve(auth.currentUser);
					const unsubscribe = auth.onAuthStateChanged((u) => {
						unsubscribe();
						resolve(u);
					});
				});
				if (!user) return;
				const token = await user.getIdToken();
				// `cerradasDesdeMinutos` trae tambien las que el medico acaba de
				// resolver. Sin eso, el apto cerrado sólo existía en el evento de
				// socket: si el evento no llegaba, no había forma de recuperarlo.
				const res = await fetch(
					`${operation_url}/active-user-operations?cerradasDesdeMinutos=10`,
					{ headers: { Authorization: `Bearer ${token}` } }
				);
				if (!res.ok) return;
				const operations: IOperation[] = await res.json();

				const activeCalls: PendingCall[] = operations
					.filter(
						(op) =>
							op.status === "in-review" &&
							op.appointment_call &&
							op.appointment_call.token
					)
					.map((op) => ({
						id: String(op.appointment_call_id ?? `hydrated-${op.id}`),
						operationId: op.id,
						doctorName: op.doctor_name ?? "",
						inCall: true,
						status: "in-progress" as const,
						timestamp: Date.now(),
					}));

				/**
				 * Las que el médico cerró recién, reconstruidas desde el servidor.
				 *
				 * Es el mismo aviso que arma `handleFinished` con el evento de
				 * socket, pero por consulta. Se saltean las que el enfermero ya
				 * cerró: la tarjeta se descarta una vez y no vuelve.
				 */
				const recienCerradas: PendingCall[] = operations
					.filter(
						(op) =>
							op.status === "finished" &&
							op.apto !== null &&
							op.apto !== undefined &&
							!descartadas.current.has(op.id)
					)
					.map((op) => ({
						id: `finished-${op.id}`,
						operationId: op.id,
						doctorName: op.doctor_name ?? "",
						inCall: false,
						status: "finished" as const,
						apto: op.apto ?? undefined,
						operator: op.user,
						timestamp: Date.now(),
					}));

				// Marca el ritmo de la relectura: rápido mientras haya algo en manos
				// del médico, lento cuando no hay nada que esperar.
				hayEsperando.current = operations.some(
					(op) =>
						op.status === "to-apt-review" ||
						op.status === "to-apt-review-call-requested" ||
						op.status === "in-review"
				);

				const activeOperationIds = new Set(activeCalls.map((c) => c.operationId));
				setCalls((prev) => {
					// Remove stale in-progress calls no longer active on backend
					const stillValid = prev.filter(
						(c) => c.status === "finished" || activeOperationIds.has(c.operationId)
					);
					// Add new calls not already in state
					const existingIds = new Set(stillValid.map((c) => c.operationId));
					const newCalls = [...activeCalls, ...recienCerradas].filter(
						(c) => !existingIds.has(c.operationId)
					);
					const merged = newCalls.length > 0 ? [...stillValid, ...newCalls] : stillValid;
					return merged.length !== prev.length || newCalls.length > 0 ? merged : prev;
				});
				return true;
			} catch {
				// Silent fail — socket events will still work
				return false;
			}
		};

		hydrate();

		// Re-sync when socket reconnects (missed events during disconnect)
		socket.on("connect", hydrate);

		// Re-sync when tab becomes visible (browsers throttle sockets in background)
		const handleVisibility = () => {
			if (document.visibilityState === "visible") hydrate();
		};
		document.addEventListener("visibilitychange", handleVisibility);

		/**
		 * Red de abajo: releer el estado cada 20 segundos.
		 *
		 * Hasta acá el aviso de llamada dependía sólo del socket, y el socket no
		 * es confiable por dos razones independientes:
		 *
		 * - Las salas de socket.io viven en la memoria de cada instancia del
		 *   backend y no hay adaptador compartido. El enfermero se suscribe con
		 *   `apt-review-notification-subscription` a una sala de UNA instancia; la
		 *   acción del médico llega a la que le tocó a su HTTP. Desde que el
		 *   backend tiene 3 instancias garantizadas (7/9, 13:08Z), que coincidan
		 *   es cuestión de suerte.
		 * - El `timeoutSeconds` de Cloud Run está en 300, así que el websocket se
		 *   corta cada cinco minutos.
		 *
		 * Ese corte es lo que hoy salva la situación por accidente: al reconectar
		 * corre `hydrate` y la llamada aparece. Por eso el reporte del 7/9 decía
		 * "demora unos minutos": el aviso llegaba, en promedio, dos minutos y
		 * medio tarde.
		 *
		 * Veinte segundos y no treinta como en la cola del médico porque acá hay
		 * alguien esperando del otro lado de una videollamada en curso. El
		 * endpoint pesa 0,4 KB y responde en 66 ms.
		 *
		 * Las tres guardas de abajo son lo que `refetchInterval` de react-query
		 * daría gratis. No se usa react-query acá porque esto no es una query:
		 * `hydrate` mezcla su resultado con el estado que traen los eventos de
		 * socket, y convertirlo es un refactor de verdad. Ese sigue siendo el
		 * destino correcto; esto es el atajo honesto mientras tanto.
		 */
		/**
		 * Ritmo de la relectura, no un intervalo fijo.
		 *
		 * El enfermero sólo necesita esto mientras espera al médico, y esa
		 * ventana es corta: medida el 7/9, la mediana entre que sale de
		 * enfermería y queda cerrado el apto es de **26 segundos**. Un intervalo
		 * fijo tiene lo peor de los dos lados: consulta todo el día para un
		 * evento que ocurre una vez por control, y cuando ocurre igual hace
		 * esperar.
		 *
		 * Así, en cambio, consulta cada 15 segundos mientras hay algo en manos
		 * del médico, y cada 60 el resto del tiempo. Ese mínimo no es de adorno:
		 * es lo que hace que el ciclo entre en modo rápido cuando el enfermero
		 * pide la revisión, sin depender de que un evento se lo avise.
		 *
		 * Se reprograma con `setTimeout` en vez de `setInterval` porque el
		 * período cambia, y de paso no puede solaparse: el próximo se agenda
		 * recién cuando el anterior terminó.
		 */
		const RITMO_ESPERANDO = 15_000;
		const RITMO_EN_REPOSO = 60_000;

		let saltarLaProxima = false;
		let temporizador: ReturnType<typeof setTimeout> | null = null;
		let cancelado = false;

		const agendar = () => {
			if (cancelado) return;
			temporizador = setTimeout(
				releerYAgendar,
				hayEsperando.current ? RITMO_ESPERANDO : RITMO_EN_REPOSO
			);
		};

		const releerYAgendar = async () => {
			// Con la pestaña de fondo el enfermero no está mirando, y al volver
			// `handleVisibility` dispara una relectura inmediata: pausar no pierde
			// nada y evita consultar de gusto.
			if (document.visibilityState !== "visible") return agendar();

			// Si la anterior falló, esperar un ciclo antes de volver a golpear.
			if (saltarLaProxima) {
				saltarLaProxima = false;
				return agendar();
			}

			saltarLaProxima = !(await hydrate());
			agendar();
		};

		agendar();

		const handleUpdateStatus = async (
			data: Omit<progressStatus, "status"> & { targetUserId?: string }
		) => {
			const myUid = auth?.currentUser?.uid;
			/*
			 * Falla CERRADO: sin sesión no se muestra nada.
			 *
			 * Antes la condición era `data.targetUserId && myUid && ...`, que se
			 * corta cuando `myUid` es `undefined` —o sea sin sesión— y el evento
			 * quedaba aceptado. Como `CallingCard` se monta en el root layout, la
			 * pantalla de login recibía y mostraba el aviso de la evaluación de
			 * **otra persona**, con nombre, legajo y si quedó apta.
			 *
			 * Reportado el 10-sep desde Service: quedándose en
			 * `/usuarios/acceso` aparece el modal "Nueva revisión finalizada".
			 */
			if (!myUid) return;
			if (data.targetUserId && data.targetUserId !== myUid) return;

			setCalls((prev) => {
				const existing = prev.findIndex((c) => c.id === data.appointment_call_id);
				const entry: PendingCall = {
					id: data.appointment_call_id,
					operationId: data.operationId,
					doctorName: data.doctorName,
					inCall: data.inCall,
					status: "in-progress",
					timestamp: Date.now(),
				};
				if (existing >= 0) {
					const next = [...prev];
					next[existing] = entry;
					return next;
				}
				return [...prev, entry];
			});

			invalidarListadoDeOperaciones(queryClient);
			await revalidateGetOperationsByMe();
		};

		const handleFinished = async (
			data: Omit<finishedStatus, "status"> & { targetUserId?: string }
		) => {
			const myUid = auth?.currentUser?.uid;
			/*
			 * Falla CERRADO: sin sesión no se muestra nada.
			 *
			 * Antes la condición era `data.targetUserId && myUid && ...`, que se
			 * corta cuando `myUid` es `undefined` —o sea sin sesión— y el evento
			 * quedaba aceptado. Como `CallingCard` se monta en el root layout, la
			 * pantalla de login recibía y mostraba el aviso de la evaluación de
			 * **otra persona**, con nombre, legajo y si quedó apta.
			 *
			 * Reportado el 10-sep desde Service: quedándose en
			 * `/usuarios/acceso` aparece el modal "Nueva revisión finalizada".
			 */
			if (!myUid) return;
			if (data.targetUserId && data.targetUserId !== myUid) return;

			setCalls((prev) => {
				const existing = prev.findIndex((c) => c.operationId === data.operationId);
				const entry: PendingCall = {
					id: existing >= 0 ? prev[existing].id : `finished-${data.operationId}`,
					operationId: data.operationId,
					doctorName: existing >= 0 ? prev[existing].doctorName : "",
					inCall: false,
					status: "finished",
					apto: data.apto,
					operator: data.operator,
					timestamp: Date.now(),
				};
				if (existing >= 0) {
					const next = [...prev];
					next[existing] = entry;
					return next;
				}
				return [...prev, entry];
			});

			invalidarListadoDeOperaciones(queryClient);
			await revalidateGetOperationsByMe();
		};

		const handleNewOperations = () => {
			invalidarListadoDeOperaciones(queryClient);
		};

		socket.on("apt-review-update-status", handleUpdateStatus);
		socket.on("apt-review-finished", handleFinished);
		socket.on("new-operations-appointments", handleNewOperations);

		return () => {
			cancelado = true;
			if (temporizador) clearTimeout(temporizador);
			socket.off("connect", hydrate);
			socket.off("apt-review-update-status", handleUpdateStatus);
			socket.off("apt-review-finished", handleFinished);
			socket.off("new-operations-appointments", handleNewOperations);
			document.removeEventListener("visibilitychange", handleVisibility);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [socket]);

	return { calls, dismissCall };
}
